/**
 * Phase 8 — Cross-competition league analytics.
 *
 * All functions are pure aggregations over the in-memory store. They do
 * NOT call out to a remote API; the future D1 swap (Phase 5) will replace
 * the data sources but keep these signatures.
 */

import type { Id, Profile, ScoreEvent, StandingsRecord, Team, Tournament } from '../models';
import { computePlayerStats, type PlayerStat } from './scoringEngine';
import { getDb } from './store';

export interface PlayerCareerStats {
  profileId: Id;
  displayName: string;
  competitionsPlayed: number;
  gamesPlayed: number;
  goals: number;
  assists: number;
  points: number;
  shots: number;
  penaltyMinutes: number;
  gameWinningGoals: number;
  shootoutAttempts: number;
  shootoutGoals: number;
  /** Per-competition breakdown (newest first). */
  byCompetition: Array<{ competitionId: Id; competitionName: string; stats: PlayerStat }>;
}

export function getPlayerCareerStats(profileId: Id): PlayerCareerStats | null {
  const db = getDb();
  const profile = db.profiles.find((p) => p.id === profileId);
  if (!profile) return null;
  const players = db.players.filter((p) => p.profileId === profileId);
  if (players.length === 0) return null;
  const playerIds = new Set(players.map((p) => p.id));
  const competitionsTouched = new Set<Id>();
  const breakdown: PlayerCareerStats['byCompetition'] = [];
  let goals = 0,
    assists = 0,
    shots = 0,
    pims = 0,
    gwg = 0,
    soAttempts = 0,
    soGoals = 0,
    gp = 0;
  for (const t of db.tournaments) {
    const events = db.scoreEvents.filter((e) => e.gameId && db.games.some((g) => g.id === e.gameId && g.tournamentId === t.id));
    if (events.length === 0) continue;
    const games = db.games.filter((g) => g.tournamentId === t.id);
    const rosterEntries = db.rosterEntries.filter((r) => r.tournamentId === t.id && playerIds.has(r.playerId));
    if (rosterEntries.length === 0) continue;
    const stats = computePlayerStats({ tournamentId: t.id, games, events, rosterEntries });
    const mine = stats.find((s) => playerIds.has(s.playerId));
    if (!mine) continue;
    competitionsTouched.add(t.id);
    breakdown.push({ competitionId: t.id, competitionName: t.name, stats: mine });
    goals += mine.goals;
    assists += mine.assists;
    shots += mine.shots;
    pims += mine.penaltyMinutes;
    gwg += mine.gameWinningGoals;
    soAttempts += mine.shootoutAttempts;
    soGoals += mine.shootoutGoals;
    gp += mine.gamesPlayed;
  }
  breakdown.sort((a, b) => b.competitionName.localeCompare(a.competitionName));
  return {
    profileId,
    displayName: profile.displayName,
    competitionsPlayed: competitionsTouched.size,
    gamesPlayed: gp,
    goals,
    assists,
    points: goals + assists,
    shots,
    penaltyMinutes: pims,
    gameWinningGoals: gwg,
    shootoutAttempts: soAttempts,
    shootoutGoals: soGoals,
    byCompetition: breakdown,
  };
}

export interface LeaderboardRow {
  profileId: Id;
  displayName: string;
  teamName?: string;
  goals: number;
  assists: number;
  points: number;
  shots: number;
  penaltyMinutes: number;
  gameWinningGoals: number;
  shootoutAttempts: number;
  shootoutGoals: number;
  gamesPlayed: number;
}

export type LeaderboardSort = 'points' | 'goals' | 'assists' | 'penaltyMinutes' | 'shots' | 'shootoutGoals';

export interface LeaderboardOptions {
  /** Limit to a single competition. Omit for league-wide. */
  competitionId?: Id;
  sort?: LeaderboardSort;
  limit?: number;
}

export function getLeaderboard(opts: LeaderboardOptions = {}): LeaderboardRow[] {
  const db = getDb();
  const profileById = new Map(db.profiles.map((p) => [p.id, p]));
  const teamById = new Map(db.teams.map((t) => [t.id, t]));
  const tournaments = opts.competitionId
    ? db.tournaments.filter((t) => t.id === opts.competitionId)
    : db.tournaments;

  const acc = new Map<Id, LeaderboardRow>();
  for (const t of tournaments) {
    const events = db.scoreEvents.filter((e) => db.games.some((g) => g.id === e.gameId && g.tournamentId === t.id));
    if (events.length === 0) continue;
    const games = db.games.filter((g) => g.tournamentId === t.id);
    const rosterEntries = db.rosterEntries.filter((r) => r.tournamentId === t.id);
    const stats = computePlayerStats({ tournamentId: t.id, games, events, rosterEntries });
    for (const s of stats) {
      const player = db.players.find((p) => p.id === s.playerId);
      if (!player) continue;
      const profile = profileById.get(player.profileId);
      if (!profile) continue;
      const team = teamById.get(s.teamId);
      const row =
        acc.get(profile.id) ??
        ({
          profileId: profile.id,
          displayName: profile.displayName,
          teamName: team?.name,
          goals: 0,
          assists: 0,
          points: 0,
          shots: 0,
          penaltyMinutes: 0,
          gameWinningGoals: 0,
          shootoutAttempts: 0,
          shootoutGoals: 0,
          gamesPlayed: 0,
        } as LeaderboardRow);
      row.goals += s.goals;
      row.assists += s.assists;
      row.shots += s.shots;
      row.penaltyMinutes += s.penaltyMinutes;
      row.gameWinningGoals += s.gameWinningGoals;
      row.shootoutAttempts += s.shootoutAttempts;
      row.shootoutGoals += s.shootoutGoals;
      row.gamesPlayed += s.gamesPlayed;
      row.points = row.goals + row.assists;
      acc.set(profile.id, row);
    }
  }
  const sortKey: LeaderboardSort = opts.sort ?? 'points';
  const rows = Array.from(acc.values()).sort((a, b) => {
    if (sortKey === 'penaltyMinutes') return a.penaltyMinutes - b.penaltyMinutes; // fewest first
    return (b[sortKey] ?? 0) - (a[sortKey] ?? 0);
  });
  return opts.limit ? rows.slice(0, opts.limit) : rows;
}

export interface TeamHistoryRow {
  competitionId: Id;
  competitionName: string;
  teamId: Id;
  rank?: number;
  wins: number;
  losses: number;
  ties: number;
  goalsFor: number;
  goalsAgainst: number;
}

export function getTeamHistoryForFranchise(franchiseId: Id): TeamHistoryRow[] {
  const db = getDb();
  const teams = db.teams.filter((t) => t.franchiseId === franchiseId);
  if (teams.length === 0) return [];
  const out: TeamHistoryRow[] = [];
  for (const team of teams) {
    const tournament = db.tournaments.find((t) => t.id === team.tournamentId);
    if (!tournament) continue;
    const standing = db.standings.find((s) => s.teamId === team.id);
    out.push({
      competitionId: tournament.id,
      competitionName: tournament.name,
      teamId: team.id,
      rank: standing?.rank,
      wins: standing?.wins ?? 0,
      losses: standing?.losses ?? 0,
      ties: standing?.ties ?? 0,
      goalsFor: standing?.goalsFor ?? 0,
      goalsAgainst: standing?.goalsAgainst ?? 0,
    });
  }
  return out.sort((a, b) => b.competitionName.localeCompare(a.competitionName));
}

/** Helpers re-exported for UI to type rows. */
export type { Profile, Team, Tournament, ScoreEvent, StandingsRecord };
